import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import {
  ChevronDownIcon,
  UserIcon,
  SunIcon,
  BoltIcon,
  SparklesIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../catalyst-components/button';
import { BaseContext } from '../../shared/base-context';
import { useStableQuery } from '../../shared/hooks/useStableQuery';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { parseMarkdown } from '../../shared/helpers/markdown';
import { InnerWeatherWidget } from '../../components/InnerWeatherWidget';
import { DeleteAccountDialog } from '../../shared/components/DeleteAccountDialog';
import { useLocalStorageImage } from '../../shared/hooks/useLocalStorageImage';
import { useNavigate } from 'react-router-dom';
import { MainRoutes } from '../../shared/models';
import { FAQHelpIcon } from '../../shared/components/FAQHelpIcon';

const formatValue = (value: string | undefined, notProvidedText: string = 'Not provided') => {
  if (!value) return `<span class="text-gray-400 italic">${notProvidedText}</span>`;

  // Check if the value contains semicolons
  if (value.includes(';')) {
    // Split by semicolon, trim each item, and convert to markdown list
    const items = value.split(';')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => `* ${item}`)
      .join('\n');
    return parseMarkdown(items);
  }

  return parseMarkdown(value);
};

// Group fields into logical resume sections
const groupFieldsIntoSections = (fields: { label: string; value: string }[]) => {
  const sections = {
    personal: fields.filter(field => 
      ['Legal Name', 'Nickname', 'State'].includes(field.label)
    ),
    background: fields.filter(field => 
      ['Academic Background', 'Work History'].includes(field.label)
    ),
    skills: fields.filter(field => 
      ['Skills'].includes(field.label)
    ),
    personalTraits: fields.filter(field => 
      ['Interests', 'Values', 'Strengths', 'Weaknesses', 'Personality'].includes(field.label)
    ),
    workStyle: fields.filter(field => 
      ['Communication Style', 'Work Style', 'Learning Style'].includes(field.label)
    ),
    career: fields.filter(field => 
      ['Career Goals', 'Reason for Job Search', 'Search Phase'].includes(field.label)
    ),
    search: fields.filter(field => 
      ['How Did You Hear About Us', 'Job Search Duration', 'Disliked Aspects of Job Search', 'How Ready to Start Job'].includes(field.label)
    )
  };

  return sections;
};

const SectionHeader = ({ title, icon }: { title: string; icon: React.ReactNode }) => (
  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-border/50">
    <span className="text-muted-foreground">{icon}</span>
    <h2 className="text-lg font-semibold text-foreground">{title}</h2>
  </div>
);

const FieldItem = ({ label, value }: { label: string; value: string }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-4 last:mb-0">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</h4>
      <div
        className="text-foreground text-sm leading-relaxed prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{
          __html: formatValue(value, t('profile.notProvided'))
        }}
      />
    </div>
  );
};

export const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { convexUserId, user } = useContext(BaseContext);
  const userMetadata = useStableQuery(api.user_metadata.getUserMetadata, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const userProgress = useStableQuery(api.user_progress.getByUserId, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const scoreHistory = useStableQuery(api.user_progress.getScoreHistory, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const completedSessions = useStableQuery(api.completed_sessions.listByUser, convexUserId ? { user_id: convexUserId as Id<"users">, limit: 1 } : "skip");
  const hasCompletedSessions = completedSessions && completedSessions.length > 0;
  const profileImage = useLocalStorageImage({
    url: user?.imageUrl || '',
    alt: user?.fullName || 'Profile',
    className: "h-20 w-20 rounded-full border-4 border-primary/20 object-cover"
  });
  const [metadataFields, setMetadataFields] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);

  useEffect(() => {
    if (userMetadata === undefined) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
    const fields = [
      ...(userMetadata?.legal_name ? [{ label: 'Legal Name', value: userMetadata.legal_name }] : []),
      ...(userMetadata?.nickname ? [{ label: 'Nickname', value: userMetadata.nickname }] : []),
      ...(userMetadata?.state ? [{ label: 'State', value: userMetadata.state }] : []),
      ...(userMetadata?.academic_background ? [{ label: 'Academic Background', value: userMetadata.academic_background }] : []),
      ...(userMetadata?.work_history ? [{ label: 'Work History', value: userMetadata.work_history }] : []),
      ...(userMetadata?.skills ? [{ label: 'Skills', value: userMetadata.skills }] : []),
      ...(userMetadata?.interests ? [{ label: 'Interests', value: userMetadata.interests }] : []),
      ...(userMetadata?.values ? [{ label: 'Values', value: userMetadata.values }] : []),
      ...(userMetadata?.strengths ? [{ label: 'Strengths', value: userMetadata.strengths }] : []),
      ...(userMetadata?.weaknesses ? [{ label: 'Weaknesses', value: userMetadata.weaknesses }] : []),
      ...(userMetadata?.personality ? [{ label: 'Personality', value: userMetadata.personality }] : []),
      ...(userMetadata?.communication_style ? [{ label: 'Communication Style', value: userMetadata.communication_style }] : []),
      ...(userMetadata?.work_style ? [{ label: 'Work Style', value: userMetadata.work_style }] : []),
      ...(userMetadata?.learning_style ? [{ label: 'Learning Style', value: userMetadata.learning_style }] : []),
      ...(userMetadata?.career_goals ? [{ label: 'Career Goals', value: userMetadata.career_goals }] : []),
      ...(userMetadata?.reason_for_search ? [{ label: 'Reason for Job Search', value: userMetadata.reason_for_search }] : []),
      ...(userMetadata?.how_did_you_hear_about_us ? [{ label: 'How Did You Hear About Us', value: userMetadata.how_did_you_hear_about_us }] : []),
      ...(userMetadata?.search_duration ? [{ label: 'Job Search Duration', value: userMetadata.search_duration }] : []),
      ...(userMetadata?.disliked_aspects_of_search ? [{ label: 'Disliked Aspects of Job Search', value: userMetadata.disliked_aspects_of_search }] : []),
      ...(userMetadata?.how_ready_to_start_search ? [{ label: 'How Ready to Start Job', value: userMetadata.how_ready_to_start_search }] : []),
      ...(userMetadata?.irl_search_phase ? [{ label: 'Search Phase', value: userMetadata.irl_search_phase }] : [])
    ];
    setMetadataFields(fields);
  }, [userMetadata]);

  const sections = groupFieldsIntoSections(metadataFields);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="max-w-4xl mx-auto px-6 pt-8">
          <Button
            color="light"
            onClick={() => navigate(MainRoutes.Progress)}
            className="mb-4"
          >
            {t('common.backHome')}
          </Button>
          <div className="flex items-center justify-center mt-12">
            <div className="text-center animate-fade-in">
              <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-12 w-12 mx-auto mb-4" />
              <p className="text-muted-foreground">{t('profile.loadingMessage')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!metadataFields.length) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="max-w-4xl mx-auto px-6 pt-8">
          <Button
            color="light"
            onClick={() => navigate(MainRoutes.Progress)}
            className="mb-4"
          >
            {t('common.backHome')}
          </Button>
          <div className="text-center max-w-md mx-auto p-8 animate-fade-in">
            <ClipboardDocumentListIcon className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-foreground mb-4">{t('profile.incompleteHeading')}</h2>
            <p className="text-muted-foreground mb-6">{t('profile.incompleteDescription')}</p>
            <div className="bg-card border-2 border-border/50 rounded-xl p-5">
              <p className="text-muted-foreground text-sm">
                <strong className="text-foreground">Tip:</strong> {t('profile.incompleteTip')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary/5 to-accent/5 border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center animate-fade-in">
          <div className="mb-4 flex justify-start">
            <Button
              color="light"
              onClick={() => navigate(MainRoutes.Progress)}
            >
              {t('common.backHome')}
            </Button>
          </div>
          <div className="mb-4 flex justify-center">
            {profileImage || <UserIcon className="h-20 w-20 text-muted-foreground" />}
          </div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            {t('profile.heading')}
          </h1>
          <p className="text-xl text-muted-foreground">{t('profile.subheading')}</p>
          <div className="mt-3">
            <FAQHelpIcon anchor="account" size="md" tooltipText="What is My Info?" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Personal Info, Inner Weather & Skills */}
          <div className="lg:col-span-1 space-y-6">
            {/* Personal Information */}
            {sections.personal.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionPersonalInfo')} icon={<UserIcon className="h-6 w-6" />} />
                <div className="space-y-3">
                  {sections.personal.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}

            {/* Inner Weather */}
            {userProgress && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionInnerWeather')} icon={<SunIcon className="h-6 w-6" />} />
                <InnerWeatherWidget
                  a={userProgress.n_score}
                  b={userProgress.wb_score}
                  c={userProgress.se_score}
                  variant="compact"
                  trend={scoreHistory ?? undefined}
                  hideDiscuss={!hasCompletedSessions}
                />
              </div>
            )}

            {/* Skills */}
            {sections.skills.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionYourSkills')} icon={<BoltIcon className="h-6 w-6" />} />
                <div className="space-y-3">
                  {sections.skills.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}

            {/* Personal Traits */}
            {sections.personalTraits.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionYourTraits')} icon={<SparklesIcon className="h-6 w-6" />} />
                <div className="space-y-3">
                  {sections.personalTraits.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Background */}
            {sections.background.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionYourBackground')} icon={<AcademicCapIcon className="h-6 w-6" />} />
                <div className="space-y-4">
                  {sections.background.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}

            {/* Work Style */}
            {sections.workStyle.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionYourWorkStyle')} icon={<BriefcaseIcon className="h-6 w-6" />} />
                <div className="space-y-4">
                  {sections.workStyle.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}

            {/* Career Goals */}
            {sections.career.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionYourCareerGoals')} icon={<RocketLaunchIcon className="h-6 w-6" />} />
                <div className="space-y-4">
                  {sections.career.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}

            {/* Job Search */}
            {sections.search.length > 0 && (
              <div className="bg-card rounded-xl shadow-sm border-2 border-border/50 p-6">
                <SectionHeader title={t('profile.sectionYourJobSearch')} icon={<MagnifyingGlassIcon className="h-6 w-6" />} />
                <div className="space-y-4">
                  {sections.search.map((field) => (
                    <FieldItem key={field.label} label={field.label} value={field.value} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <div className="bg-muted border border-border/50 rounded-xl p-5 max-w-2xl mx-auto">
            <p className="text-muted-foreground text-sm">
              {t('profile.footerNote')}
            </p>
          </div>
        </div>

        {/* Account Management (Danger Zone) */}
        <div className="mt-8 max-w-2xl mx-auto">
          <Disclosure>
            <DisclosureButton className="flex w-full items-center justify-between rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-600 hover:bg-red-100 transition-colors">
              <span className="font-medium">{t('profile.accountManagementHeading')}</span>
              <ChevronDownIcon className="h-4 w-4 transition-transform ui-open:rotate-180" />
            </DisclosureButton>
            <DisclosurePanel className="mt-2 rounded-xl border border-red-200 bg-white p-5">
              <p className="text-sm text-muted-foreground mb-4">
                {t('profile.accountManagementDescription')}
              </p>
              <button
                onClick={() => setShowDeleteAccountDialog(true)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
              >
                {t('profile.deleteAccountButton')}
              </button>
            </DisclosurePanel>
          </Disclosure>
        </div>

        <DeleteAccountDialog
          open={showDeleteAccountDialog}
          onClose={() => setShowDeleteAccountDialog(false)}
        />
      </div>
    </div>
  );
};
